import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMediaByAny,
  selectAllMedia,
  selectFetchedMedia,
  selectMediaIsLoading,
  selectMediaTotal,
} from "@/store/redux/slices/media/media_slice";
import { useLoader } from "@/hooks/LoaderProvider";
import { useNavigate } from "react-router-dom";
import { DownOutlined, PlusOutlined, Dropdown } from "@/config/antd";
import PageHeader from "@/components/layout/Header/Page_Header";
import Breadcrumb from "@/components/specific/Breadcrumb";
import Button from "@/components/common/Button";
import SearchFilterBar from "@/components/specific/Search_&_Filter";
import CustomPagination from "@/components/specific/Pagination";
import MediaCard from "@/pages/media_manager/components/Media_Card";
import EmptyState from "@/pages/media_manager/components/Empty_State";
import MediaDialog from "@/pages/media_manager/components/Media_Dialog";
import MediaHomeTable from "@/pages/media_manager/components/MediaHomeTable";
import TrashIcon from "@/assets/icons/web_Icons/trash_red.svg";
import Success from "@/components/common/alerts/Success";
import { deleteMediaAsync } from "@/store/redux/slices/media/media_slice";
import { getOrganizationId } from "@/utils/token_functions";
import Delete from "@/components/common/alerts/Delete";
import { selectUserProfile } from "@/store/redux/slices/user/user_slice";

const MediaManagerHome = () => {
  const dispatch = useDispatch();
  const mediaList = useSelector(selectAllMedia);
  const fetchedMediaByOrg = useSelector(selectFetchedMedia);
  const isLoading = useSelector(selectMediaIsLoading);
  const totalMediaCount = useSelector(selectMediaTotal);
  const [searchValue, setSearchValue] = useState("");
  const [filterValue, setFilterValue] = useState("Today");
  const [totalFiles, setTotalFiles] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMediaDialogVisible, setIsMediaDialogVisible] = useState(false);
  const pageSize = 10;
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("all");
  const [activeTypeFilter, setActiveTypeFilter] = useState("all");
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [deletedMediaName, setDeletedMediaName] = useState("");
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedMediaToDelete, setSelectedMediaToDelete] = useState(null);
  const userProfile = useSelector(selectUserProfile);

  const getMediaToUse = useCallback(() => {
    const fetched = fetchedMediaByOrg ?? [];
    const list = mediaList ?? [];
    return fetched.length > 0 ? fetched : list;
  }, [fetchedMediaByOrg, mediaList]);

  // Update total files count based on available sources
  useEffect(() => {
    const mediaCount = getMediaToUse().length;
    setTotalFiles(mediaCount > 0 ? mediaCount : totalMediaCount);
  }, [getMediaToUse, totalMediaCount]);

  const handleRefresh = useCallback(async () => {
    // 1. Reset pagination to first page
    setCurrentPage(1);

    // 2. Clear the search term
    setSearchValue("");

    // 3. Reset the active filter to 'all'
    setActiveFilter("all");

    // 4. Reset the display text for the filter button
    setFilterValue("Today");

    // 5. Fetch fresh data
    const organizationID = getOrganizationId();

    showLoader();
    try {
      await dispatch(
        fetchMediaByAny({
          key: "organizationID",
          value: organizationID,
        })
      ).unwrap();
    } catch (error) {
      console.error("Error refreshing media:", error);
    } finally {
      hideLoader();
    }
  }, [dispatch, showLoader, hideLoader]);

  useEffect(() => {
    const fetchMedia = async () => {
      showLoader();
      try {
        const organizationID = getOrganizationId();
        await dispatch(
          fetchMediaByAny({
            key: "organizationID",
            value: organizationID,
          })
        ).unwrap();
      } catch (error) {
        console.error("Error fetching media:", error);
      } finally {
        hideLoader();
      }
    };

    fetchMedia();
  }, [dispatch, hideLoader, showLoader]);

  // Transform data for cards (mobile view)
  const getFilteredCardsData = useCallback(() => {
    let filtered = getMediaToUse() || [];

    // Apply search filter
    if (searchValue.trim()) {
      filtered = filtered.filter((media) =>
        media.name?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    if (isLoading && filtered.length === 0) {
      return [];
    }

    // Apply status filter
    switch (activeFilter) {
      case "published":
        filtered = filtered.filter((media) => media.status === "published");
        break;
      case "draft":
        filtered = filtered.filter((media) => media.status === "draft");
        break;
      default:
        break;
    }

    // Apply media type filter
    switch (activeTypeFilter) {
      case "image":
        filtered = filtered.filter(
          (media) =>
            (media.imageFiles && media.imageFiles.length > 0) ||
            (!media.videoFiles?.length && !media.documentFiles?.length)
        );
        break;
      case "video":
        filtered = filtered.filter(
          (media) => media.videoFiles && media.videoFiles.length > 0
        );
        break;
      case "document":
        filtered = filtered.filter(
          (media) => media.documentFiles && media.documentFiles.length > 0
        );
        break;
      default:
        break;
    }

    // Transform for cards
    return filtered.map((media) => ({
      key: media._id,
      id: media._id || Math.random().toString(),
      name: media.name || "",
      title: media.name || "",
      description: media.description || "",
      date: media.createdAt
        ? new Date(media.createdAt).toLocaleDateString()
        : "",
      author: userProfile?.fullname || "System User",
      authorAvatar: userProfile?.profileImage,
      imageFiles: media.imageFiles || [],
      videoFiles: media.videoFiles || [],
      documentFiles: media.documentFiles || [],
      _id: media._id,
    }));
  }, [
    getMediaToUse,
    searchValue,
    activeFilter,
    activeTypeFilter,
    isLoading,
    userProfile,
  ]);

  // Transform data for table (desktop view)
  const getFilteredTableData = useCallback(() => {
    return getFilteredCardsData();
  }, [getFilteredCardsData]);

  // Memoize the data
  const cardData = getFilteredCardsData();
  const tableData = getFilteredTableData();

  const handleSearch = (value) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const key = e.key;
    if (["all", "image", "video", "document"].includes(key)) {
      setActiveTypeFilter(key);
      const labelMap = {
        all: "All Types",
        image: "Images",
        video: "Videos",
        document: "Documents",
      };
      setFilterValue(labelMap[key]);
    } else {
      setFilterValue(key);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleAddNew = () => {
    if (selectedFiles.length > 0) {
      // Show delete confirmation dialog
      setIsDeleteModalVisible(true);
    } else {
      setIsMediaDialogVisible(true);
    }
  };

  const handleCloseMediaDialog = () => {
    setIsMediaDialogVisible(false);
    // handleRefresh();
  };

  const handleDeleteSuccess = () => {
    handleRefresh();
  };

  const handleDelete = useCallback(async (mediaId, mediaTitle) => {
    // If called with a specific ID, it's an individual delete
    if (mediaId) {
      setSelectedMediaToDelete({ id: mediaId, title: mediaTitle });
      setIsDeleteModalVisible(true);
    }
  }, []);

  const handleBulkDelete = async () => {
    if (selectedFiles.length === 0) return;
    setIsDeleteModalVisible(false);

    showLoader();
    try {
      // Get the titles of the media being deleted for the success message
      const mediaToDelete = getMediaToUse().filter((media) =>
        selectedFiles.includes(media._id)
      );

      const mediaCount = mediaToDelete.length;

      // If only one media, use its title, otherwise use count
      if (mediaCount === 1) {
        setDeletedMediaName(mediaToDelete[0].name);
      } else {
        setDeletedMediaName(`${mediaCount} media files`);
      }

      // Delete all selected media
      await Promise.all(
        selectedFiles.map((mediaId) =>
          dispatch(deleteMediaAsync(mediaId)).unwrap()
        )
      );

      // Refresh data and reset selection
      await handleRefresh();
      setSelectedFiles([]);
      setSuccessModalVisible(true);
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      hideLoader();
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleteModalVisible(false);

    if (selectedMediaToDelete) {
      // Single media delete
      showLoader();
      try {
        await dispatch(deleteMediaAsync(selectedMediaToDelete.id)).unwrap();
        setDeletedMediaName(selectedMediaToDelete.title);
        await handleRefresh();
        setIsDeleteModalVisible(false);
        setSuccessModalVisible(true);
        setSelectedMediaToDelete(null);
      } catch (error) {
        console.error("Delete failed:", error);
      } finally {
        hideLoader();
      }
    } else {
      // Bulk delete
      await handleBulkDelete();
    }
  };

  const handleModify = useCallback(
    (mediaId) => {
      navigate(`/dashboard/media/edit/${mediaId}`);
    },
    [navigate]
  );

  const filterOptions = {
    items: [
      { key: "all", label: "All Types" },
      { key: "image", label: "Images" },
      { key: "video", label: "Videos" },
      { key: "document", label: "Documents" },
    ],
    onClick: handleFilterChange,
    className: "custom-dropdown-menu",
  };

  const moreOptionsMenu = {
    items: [
      { key: "1", label: "Export" },
      { key: "2", label: "Print" },
    ],
  };

  // Define sort options based on your filter buttons
  const sortOptions = [
    { key: "all", label: "All Files" },
    { key: "published", label: "Published" },
    { key: "draft", label: "Drafts" },
  ];

  // breadcrumb items
  const items = [{ title: "Media Manager", link: "/dashboard/media" }];

  return (
    <div className="w-full my-6">
      <Breadcrumb items={items} />
      <PageHeader title="Media Manager" />

      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-5">
          <div className="flex flex-col text-left">
            <h1 className="heading-medium text-lighterGrey">
              {getMediaToUse()?.length || 0} Total Media Files
            </h1>
            <h1 className="text-regular text-lighterGrey">
              Total number of Files Uploaded
            </h1>
          </div>
          <div className="items-center hidden gap-2 lg:flex">
            <Button
              text={`All Files (${getMediaToUse()?.length || 0})`}
              color="primary"
              className="px-2 py-1 text-sm sm:px-3 sm:py-2 sm:text-small min-w-[80px] sm:min-w-[100px]"
              iconPosition="left"
              customBgColor={activeFilter === "all" ? "primary" : "white"}
              customTextColor={activeFilter === "all" ? "white" : "primary"}
              onClick={() => setActiveFilter("all")}
            />
            <Button
              text={`Published (${
                getMediaToUse()?.filter((m) => m.status === "published")
                  .length || 0
              })`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "published" ? "primary" : "white"}
              customTextColor={
                activeFilter === "published" ? "white" : "primary"
              }
              onClick={() => setActiveFilter("published")}
            />
            <Button
              text={`Drafts (${
                getMediaToUse()?.filter((m) => m.status === "draft").length || 0
              })`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "draft" ? "primary" : "white"}
              customTextColor={activeFilter === "draft" ? "white" : "primary"}
              onClick={() => setActiveFilter("draft")}
            />
          </div>
        </div>
        <div className="items-center hidden gap-3 lg:flex">
          <Button
            text={
              selectedFiles.length !== 0 ? "DELETE SELECTED" : "ADD A NEW FILE"
            }
            icon={
              selectedFiles.length !== 0 ? (
                <img src={TrashIcon} />
              ) : (
                <PlusOutlined />
              )
            }
            color={selectedFiles.length !== 0 ? "outline" : "primary"}
            className="border border-primary font-normal text-[11px] !px-3 !py-2 h-8"
            iconPosition="left"
            customBgColor={selectedFiles.length !== 0 ? "" : "white"}
            customTextColor={selectedFiles.length !== 0 ? "red" : "primary"}
            onClick={handleAddNew}
          />
          <Dropdown menu={filterOptions} trigger={["click"]}>
            <Button
              text="FILTER BY"
              icon={<DownOutlined />}
              iconPosition="right"
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor="white"
              customTextColor="black"
            />
          </Dropdown>
        </div>
        <Button
          text="ADD NEW"
          icon={<PlusOutlined />}
          color="primary"
          className="px-2 py-1 text-small sm:px-3 sm:py-2 sm:text-small h-8 sm:h-10 min-w-[80px] sm:min-w-[100px] whitespace-nowrap lg:hidden"
          iconPosition="left"
          customBgColor="primary"
          customTextColor="white"
          onClick={handleAddNew}
        />
      </div>

      <SearchFilterBar
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        onFilterChange={(value) => {
          setActiveFilter(value);
        }}
        filterValue={filterValue}
        filterOptions={filterOptions}
        moreOptionsMenu={moreOptionsMenu}
        placeholder="Search..."
        filterType="dropdown"
        sortOptions={sortOptions}
        searchValue={searchValue}
      />

      {/* Conditional Rendering based on data and filters */}
      {!getMediaToUse()?.length ? (
        <EmptyState onSetup={handleAddNew} />
      ) : cardData.length === 0 && tableData.length === 0 ? (
        <EmptyState onSetup={handleAddNew} />
      ) : (
        <>
          {/* Desktop table view */}
          <div className="hidden lg:block">
            <MediaHomeTable
              data={tableData}
              setSelectedFiles={setSelectedFiles}
              onDelete={handleDelete}
              onModify={handleModify}
              onDeleteSuccess={handleDeleteSuccess}
            />
          </div>

          {/* Mobile card view */}
          <div className="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2 lg:hidden">
            {cardData.map((file) => {
              const { ...fileProps } = file;

              return (
                <MediaCard
                  key={file._id}
                  {...fileProps}
                  onDelete={handleDelete}
                  onModify={handleModify}
                />
              );
            })}
          </div>

          <div className="mt-6">
            <CustomPagination
              current={currentPage}
              total={totalFiles}
              pageSize={pageSize}
              onChange={handlePageChange}
              className="pb-8"
            />
          </div>
        </>
      )}

      <MediaDialog
        isVisible={isMediaDialogVisible}
        onClose={handleCloseMediaDialog}
        onSuccess={handleRefresh}
      />

      {/* Success Modal */}
      <Success
        isVisible={successModalVisible}
        onClose={() => setSuccessModalVisible(false)}
        header="Media Deleted"
        paragraph={`"${deletedMediaName}" has been successfully deleted.`}
        buttonText="Done"
        showCancelButton={false}
        onButtonClick={() => setSuccessModalVisible(false)}
      />

      {/* Delete confirmation modal */}
      <Delete
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onProceed={handleDeleteConfirm}
        header="Delete Media?"
        paragraph={
          selectedFiles.length > 0
            ? `Are you sure you want to delete ${selectedFiles.length} selected media files? This action cannot be undone.`
            : `Are you sure you want to delete "${selectedMediaToDelete?.title}"? This action cannot be undone.`
        }
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </div>
  );
};

export default MediaManagerHome;
